import { useState, useEffect, useMemo, useCallback } from "react";
import { Form } from "react-admin";
import { Link } from "react-router-dom";
import Autocomplete from "@mui/material/Autocomplete";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { makeStyles } from "@mui/styles";
import ConfirmDialog from "../../../core/ConfirmDialog";
import LoadingOverlay from "../../../core/LoadingOverlay";
import DismissibleMessage from "../DismissibleMessage";
import { ClientsDropdown, MultiProjectsDropdown } from "../DropdownV2";
import { calibrationService } from "../../../../services/CalibrationService";
import { PIT_GEOJSON_DATASET_TYPE, TILE_GENERATION_PROCESS_TYPE } from "./tileStatus";

const DEFAULT_GROUP_KEY = "default";

const ELIGIBILITY = {
    CHECKING: "checking",
    ELIGIBLE: "eligible",
    MISSING: "missing",
    ERROR: "error",
};

const ROLLUP_STATUS = {
    IDLE: "idle",
    SUBMITTING: "submitting",
    SUBMITTED: "submitted",
    FAILED: "failed",
};

const useStyles = makeStyles((theme) => ({
    formContainer: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
    },
    dropdownRow: {
        display: "flex",
        flexDirection: "row",
        justifyContent: "center",
        gap: theme.spacing(2),
        margin: "10px 0",
        width: "100%",
        maxWidth: "700px",
        [theme.breakpoints.down("sm")]: {
            flexDirection: "column",
        },
    },
    optionsRow: {
        display: "flex",
        flexDirection: "row",
        gap: theme.spacing(2),
        margin: "10px 0",
        width: "100%",
        maxWidth: "700px",
        alignItems: "center",
    },
    tableContainer: {
        width: "100%",
        maxWidth: "900px",
        margin: "16px 0",
    },
    buttonRow: {
        display: "flex",
        justifyContent: "center",
        margin: "20px 0",
    },
    statusRow: {
        display: "flex",
        alignItems: "center",
        gap: "12px",
        margin: "8px 0",
    },
}));

const eligibilityChip = (state) => {
    switch (state) {
        case ELIGIBILITY.ELIGIBLE:
            return <Chip label="Has pit-geojson" color="success" size="small" />;
        case ELIGIBILITY.MISSING:
            return <Chip label="Missing pit-geojson" color="default" size="small" />;
        case ELIGIBILITY.ERROR:
            return <Chip label="Error" color="error" size="small" />;
        case ELIGIBILITY.CHECKING:
        default:
            return <Chip label="Checking…" size="small" />;
    }
};

const rollupStatusChip = (state, error) => {
    switch (state) {
        case ROLLUP_STATUS.SUBMITTING:
            return <Chip label="Submitting…" size="small" />;
        case ROLLUP_STATUS.SUBMITTED:
            return <Chip label="Submitted — see Progress Viewer" color="primary" size="small" />;
        case ROLLUP_STATUS.FAILED:
            return <Chip label={`Failed${error ? `: ${error}` : ""}`} color="error" size="small" />;
        case ROLLUP_STATUS.IDLE:
        default:
            return null;
    }
};

export default function MultiProjectTileGen() {
    const classes = useStyles();
    const [clientName, setClientName] = useState(null);
    const [selectedProjects, setSelectedProjects] = useState([]);
    const [outputProjectName, setOutputProjectName] = useState("");
    const [outputProjectOptions, setOutputProjectOptions] = useState([]);
    const [eligibility, setEligibility] = useState({});
    const [rollupStatus, setRollupStatus] = useState({ state: ROLLUP_STATUS.IDLE });
    const [errorMessage, setErrorMessage] = useState(null);
    const [statusMessage, setStatusMessage] = useState(null);
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);

    useEffect(() => {
        setSelectedProjects([]);
        setEligibility({});
        setOutputProjectName("");
        setOutputProjectOptions([]);
        setRollupStatus({ state: ROLLUP_STATUS.IDLE });
        setStatusMessage(null);
        setErrorMessage(null);
    }, [clientName]);

    // Pull the project list for the current client so the user can either pick an
    // existing project as the rollup output target (e.g., overwrite "combined-tile"
    // tiles) or type a brand-new name. MultiProjectsDropdown also fetches this list
    // internally — kept separate here to avoid plumbing state up; small cost.
    useEffect(() => {
        if (!clientName) return undefined;
        let cancelled = false;
        (async () => {
            try {
                const response = await calibrationService.getProjectNames(clientName);
                if (cancelled) return;
                setOutputProjectOptions(response?.data || []);
            } catch {
                if (!cancelled) setOutputProjectOptions([]);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [clientName]);

    const checkEligibility = useCallback(async () => {
        if (!clientName || selectedProjects.length === 0) {
            setEligibility({});
            return;
        }
        const initial = {};
        for (const p of selectedProjects) {
            initial[p] = { state: ELIGIBILITY.CHECKING };
        }
        setEligibility(initial);

        const results = await Promise.allSettled(
            selectedProjects.map((project) =>
                calibrationService.getLatestDatasetViaSignedUrl(
                    clientName,
                    project,
                    DEFAULT_GROUP_KEY,
                    PIT_GEOJSON_DATASET_TYPE,
                    false
                )
            )
        );
        const next = {};
        results.forEach((result, idx) => {
            const project = selectedProjects[idx];
            if (result.status === "fulfilled" && result.value) {
                next[project] = { state: ELIGIBILITY.ELIGIBLE, versionDttm: result.value?.meta?.version_dttm };
            } else if (result.status === "rejected") {
                const status = result.reason?.response?.status;
                if (status === 404) {
                    next[project] = { state: ELIGIBILITY.MISSING };
                } else {
                    next[project] = {
                        state: ELIGIBILITY.ERROR,
                        error: result.reason?.message || String(result.reason),
                    };
                }
            } else {
                next[project] = { state: ELIGIBILITY.MISSING };
            }
        });
        setEligibility(next);
    }, [clientName, selectedProjects]);

    useEffect(() => {
        checkEligibility();
    }, [checkEligibility]);

    const eligibleProjects = useMemo(
        () => selectedProjects.filter((p) => eligibility[p]?.state === ELIGIBILITY.ELIGIBLE),
        [selectedProjects, eligibility]
    );

    // isRollup is driven by the user's selection, NOT eligibility — that flag controls
    // the *UI affordance* (show the output-name field, label the button "combined", etc.).
    // canSubmit is the one that waits for eligibility to resolve, since only ELIGIBLE
    // projects actually get sent to the trigger.
    const isRollup = selectedProjects.length >= 2;
    const isCheckingEligibility = Object.values(eligibility).some((e) => e?.state === ELIGIBILITY.CHECKING);
    const outputProjectInvalid = isRollup && !outputProjectName.trim();
    const inFlight = rollupStatus.state === ROLLUP_STATUS.SUBMITTING;
    const canSubmit =
        clientName && eligibleProjects.length > 0 && !inFlight && !outputProjectInvalid && !isCheckingEligibility;

    const handleSubmit = async () => {
        setConfirmModalOpen(false);
        setErrorMessage(null);
        setStatusMessage(null);
        setRollupStatus({ state: ROLLUP_STATUS.SUBMITTING });
        const primary = eligibleProjects[0];
        const additionalProjects = eligibleProjects.slice(1).map((p) => ({ clientName, projectName: p }));
        try {
            await calibrationService.triggerTileGeneration(clientName, primary, {
                additionalProjects: isRollup ? additionalProjects : [],
                outputProjectName: isRollup ? outputProjectName.trim() : null,
            });
            setRollupStatus({ state: ROLLUP_STATUS.SUBMITTED });
            setStatusMessage(
                isRollup
                    ? `Rollup submitted for ${eligibleProjects.length} project(s) → "${outputProjectName.trim()}". Check the Progress Viewer for status.`
                    : `Tile generation submitted for "${primary}". Check the Progress Viewer for status.`
            );
        } catch (err) {
            const isTimeout = err === "Network Error" || err?.code === "ERR_NETWORK";
            if (isTimeout) {
                // Submission timed out at the API gateway (expected for long-running Batch jobs).
                // The job itself is in flight; the Progress Viewer will show its real status.
                setRollupStatus({ state: ROLLUP_STATUS.SUBMITTED });
                setStatusMessage("Submission timed out at the gateway (expected). Check the Progress Viewer for status.");
            } else {
                setRollupStatus({ state: ROLLUP_STATUS.FAILED, error: err?.message || String(err) });
                setErrorMessage(`Failed to submit: ${err?.message || String(err)}`);
            }
        }
    };

    const targetProject = isRollup ? outputProjectName.trim() : eligibleProjects[0] || "";
    const progressViewerUrl = `/calibration/progressViewer?client=${encodeURIComponent(
        clientName || ""
    )}&project=${encodeURIComponent(targetProject)}&processTypes=${TILE_GENERATION_PROCESS_TYPE}`;

    const confirmMessage = [
        isRollup
            ? `Generate combined tiles for ${eligibleProjects.length} projects under client "${clientName}" → output project "${outputProjectName.trim()}"?`
            : `Regenerate tiles for client "${clientName}", project "${eligibleProjects[0]}"?`,
        "",
        ...eligibleProjects.map((p) => `• ${p}`),
    ].join("\n");

    return (
        <div style={{ paddingBottom: "200px" }}>
            <ConfirmDialog
                open={confirmModalOpen}
                title="Confirm Tile Generation"
                handleClose={() => setConfirmModalOpen(false)}
                handleConfirm={handleSubmit}
                message={confirmMessage}
            />
            <Form className={classes.formContainer}>
                <DismissibleMessage message={errorMessage} type="error" onDismiss={() => setErrorMessage(null)} />
                <DismissibleMessage message={statusMessage} type="success" onDismiss={() => setStatusMessage(null)} />
                <LoadingOverlay isLoading={rollupStatus.state === ROLLUP_STATUS.SUBMITTING} message="Submitting…" />

                <Typography
                    variant="body2"
                    style={{ textAlign: "center", maxWidth: "700px", margin: "0 auto 8px", color: "#666" }}
                >
                    Generate one combined tile dataset from many projects under a single client (e.g., a CONUS-wide
                    rollup). Pick the source projects, then name the output project that will own the combined
                    tiles. Picking just one project regenerates its own tiles.
                </Typography>
                <Typography
                    variant="caption"
                    style={{ textAlign: "center", maxWidth: "700px", margin: "0 auto 16px", color: "#888", display: "block" }}
                >
                    Rollup runs as a Batch job (~minutes). Track it in the{" "}
                    <Link
                        to={`/calibration/progressViewer?processTypes=${TILE_GENERATION_PROCESS_TYPE}`}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Progress Viewer
                    </Link>
                    . When done, check the <strong>Single Project Tiles</strong> tab for performance and general QA.
                </Typography>

                <div className={classes.dropdownRow}>
                    <ClientsDropdown
                        value={clientName}
                        setValue={setClientName}
                        styleClass={{ flex: 1, minWidth: "150px", maxWidth: "300px" }}
                    />
                    <MultiProjectsDropdown
                        clientName={clientName}
                        value={selectedProjects}
                        setValue={setSelectedProjects}
                        styleClass={{ flex: 2, minWidth: "200px", maxWidth: "400px" }}
                    />
                </div>

                {isRollup && (
                    <div className={classes.optionsRow}>
                        <Autocomplete
                            freeSolo
                            options={outputProjectOptions}
                            value={outputProjectName}
                            onChange={(_, newValue) => setOutputProjectName(newValue ?? "")}
                            onInputChange={(_, newInput) => setOutputProjectName(newInput)}
                            size="small"
                            fullWidth
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Output project name"
                                    required
                                    error={outputProjectInvalid}
                                    helperText={
                                        outputProjectInvalid
                                            ? "Required — combining multiple projects into a single named output"
                                            : "Pick an existing project to overwrite, or type a new name (e.g., conus-rollup-2026-q2)"
                                    }
                                />
                            )}
                        />
                    </div>
                )}

                {selectedProjects.length > 0 && (
                    <Paper variant="outlined" className={classes.tableContainer}>
                        <Typography variant="subtitle2" style={{ padding: "12px 16px 0" }}>
                            Source projects
                        </Typography>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Project</TableCell>
                                    <TableCell>Eligibility</TableCell>
                                    <TableCell>Latest pit-geojson</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {selectedProjects.map((project) => {
                                    const entry = eligibility[project] || { state: ELIGIBILITY.CHECKING };
                                    const greyed = entry.state !== ELIGIBILITY.ELIGIBLE;
                                    return (
                                        <TableRow key={project} sx={greyed ? { opacity: 0.55 } : undefined}>
                                            <TableCell>{project}</TableCell>
                                            <TableCell>{eligibilityChip(entry.state)}</TableCell>
                                            <TableCell>{entry.versionDttm || "—"}</TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </Paper>
                )}

                {rollupStatus.state !== ROLLUP_STATUS.IDLE && (
                    <div className={classes.statusRow}>
                        <Typography variant="subtitle2">Rollup status:</Typography>
                        {rollupStatusChip(rollupStatus.state, rollupStatus.error)}
                        {targetProject && (
                            <Link to={progressViewerUrl} target="_blank" rel="noopener noreferrer">
                                View in Progress Viewer
                            </Link>
                        )}
                    </div>
                )}

                <div className={classes.buttonRow}>
                    <Button
                        variant="contained"
                        disabled={!canSubmit}
                        onClick={() => setConfirmModalOpen(true)}
                    >
                        {isCheckingEligibility
                            ? "Checking eligibility…"
                            : isRollup
                            ? `Generate combined tiles for ${eligibleProjects.length} project(s)`
                            : eligibleProjects.length === 1
                            ? `Generate tiles for "${eligibleProjects[0]}"`
                            : "Generate tiles"}
                    </Button>
                </div>
            </Form>
        </div>
    );
}
